import React from 'react';
import { Card, Typography, Space, Tag, Spin, Alert, List, Button } from 'antd';
import { MedicineBoxOutlined, CalendarOutlined, UserOutlined, EyeOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setSelectedPrescriptionId, setPrescriptionLoading, setPrescriptionDetails, setPrescriptionError } from '../store/slices/prescriptionSlice';
import PrescriptionService from '../services/prescriptionService';

const { Title, Text } = Typography;

const PrescriptionList: React.FC = () => {
  const dispatch = useDispatch();
  const { prescriptionList, loading, error, selectedPrescriptionId, prescriptionDetails } = useSelector(
    (state: RootState) => state.prescription
  );

  const handlePrescriptionSelect = async (prescriptionId: string) => {
    try {
      // If clicking the same prescription, toggle the details
      if (selectedPrescriptionId === prescriptionId) {
        // Toggle: if details are showing, clear them; if not showing, fetch them
        if (prescriptionDetails) {
          dispatch(setPrescriptionDetails(null));
          dispatch(setSelectedPrescriptionId(null));
        } else {
          // This shouldn't happen as selectedPrescriptionId should only be set when details exist
          // But keeping this for safety
          dispatch(setSelectedPrescriptionId(null));
        }
        return;
      }

      // If clicking a different prescription, fetch its details
      dispatch(setSelectedPrescriptionId(prescriptionId));
      dispatch(setPrescriptionLoading(true));
      dispatch(setPrescriptionError(null));

      // Find the prescription details from the list
      const prescription = prescriptionList?.find(p => p.prescriptionId === prescriptionId);
      if (!prescription) return;

      // Fetch detailed prescription information
      const prescriptionResponse = await PrescriptionService.getPrescriptionDetails(
        prescription.patientId,
        prescriptionId
      );

      // Dispatch the detailed prescription data
      dispatch(setPrescriptionDetails(prescriptionResponse));
      
    } catch (error: any) {
      console.error('Error fetching prescription details:', error);
      dispatch(setPrescriptionError(error.message || 'Failed to fetch prescription details'));
      dispatch(setPrescriptionDetails(null));
    } finally {
      dispatch(setPrescriptionLoading(false));
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: '16px' }}>
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <Spin size="small" /> <Text type="secondary">Loading prescriptions...</Text>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card size="small" style={{ marginTop: '16px' }}>
        <Alert
          message="Error Loading Prescriptions"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (!prescriptionList || prescriptionList.length === 0) {
    return (
      <Card size="small" style={{ marginTop: '16px' }}>
        <Alert
          message="No Prescriptions Found"
          description="This patient has no prescription records available."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  

  return (
    <Card size="small" style={{ marginTop: '16px' }}>
      <Title level={5} style={{ margin: 0, marginBottom: '16px' }}>
        <MedicineBoxOutlined /> Prescription History
      </Title>

      <List
        size="small"
        dataSource={prescriptionList}
        renderItem={(prescription) => (
          <List.Item
            key={prescription.id}
            style={{
              cursor: 'pointer',
              padding: '12px',
              border: selectedPrescriptionId === prescription.prescriptionId ? '2px solid #1890ff' : '1px solid #f0f0f0',
              borderRadius: '6px',
              marginBottom: '8px',
              backgroundColor: selectedPrescriptionId === prescription.prescriptionId ? '#f6ffed' : '#fff'
            }}
            onClick={() => handlePrescriptionSelect(prescription.prescriptionId)}
            actions={[
              <Button
                key="view"
                type={selectedPrescriptionId === prescription.prescriptionId ? "primary" : "default"}
                size="small"
                icon={<EyeOutlined />}
              >
                {selectedPrescriptionId === prescription.prescriptionId ? 'Selected' : 'View'}
              </Button>
            ]}
          >
            <List.Item.Meta
              avatar={<MedicineBoxOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
              title={
                <Space>
                  <Text strong>Prescription #{prescription.prescriptionId}</Text>
                  <Tag color={prescription.status === 'active' ? 'green' : 'default'}>
                    {prescription.status}
                  </Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <CalendarOutlined />
                    <Text type="secondary">
                      Created: {formatDate(prescription.createdAt)}
                    </Text>
                  </Space>
                  {prescription.prescriptionDate && (
                    <Space>
                      <CalendarOutlined />
                      <Text type="secondary">
                        Prescription Date: {formatDate(prescription.prescriptionDate)}
                      </Text>
                    </Space>
                  )}
                  {prescription.doctorName && (
                    <Space>
                      <UserOutlined />
                      <Text type="secondary">
                        Doctor: {prescription.doctorName}
                      </Text>
                    </Space>
                  )}
                  {prescription.caseId && (
                    <Space>
                      <Text type="secondary">
                        Case ID: {prescription.caseId}
                      </Text>
                    </Space>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

export default PrescriptionList;
